import type { IRouteConfig } from '@acme/foundation';
import {
  ApplicationClientDetails,
  ApplicationLayout,
} from '@pages/application';
import { ApplicationFormation } from '@pages/applicationFormation';
import {
  ApplicationClientPage,
  ApplicationContractPage,
  ApplicationTransferPage,
  CounterpartyOfferPage,
  FormAgentInfoPage,
  ProviderContractPage,
  SendOfferPage,
  SignDocumentPage,
  TransactionIdPage,
  UploadDocumentPage,
} from '@pages/applicationProgress';
import { ApplicationLiquidityProvidersPage } from '@pages/applicationProviders';
import {
  AuthError,
  AuthPage,
  Blocked,
  BlockedUserPage,
  EcpLoginPage,
  EsiaErrors,
  EsiaLoginPage,
  Unavailable,
  WelcomePage,
} from '@pages/auth';
import { DashboardPage } from '@pages/dashboard';
import { DocumentsPage } from '@pages/documents';
import {
  Error400Page,
  Error401Page,
  Error403Page,
  Error404Page,
  Error500Page,
} from '@pages/errors/routes';
import { ExchanegeRatesCreatePage } from '@pages/exchanegeRates';
import { HelpCenterPage } from '@pages/help-center';
import { NotificationsPage } from '@pages/notifications';
import { ProfileEditPage, ProfilePage } from '@pages/profile';
import { CurrencyListPage, RatesPage } from '@pages/rates';
import {
  TicketDetailPage,
  TicketsCreatePage,
  TicketsListPage,
} from '@pages/tickets';
import { AuthClient, AuthGuard, checkToken } from '@shared/guard';
import { CenteredLayout, DefaultLayout } from '@shared/layouts';
import { Outlet, Route, Routes } from 'react-router-dom';
import { RootRedirect } from './rootRedirect';
import { BalancePage } from '@pages/balance';

export const AppRoutes: IRouteConfig = {
  auth: {
    path: '/auth',
    label: 'Авторизация',
    esia: {
      path: '/auth/esia',
      backPath: '/auth',
      label: 'Вход через ЕСИА',
    },
    ecp: {
      path: '/auth/ecp',
      backPath: '/auth',
      label: 'Вход с КЭП',
    },
    blocked: {
      path: '/auth/blocked',
      label: 'Вход временно заблокирован',
    },
    unavailable: {
      path: '/auth/unavailable',
      label: 'Вход недоступен',
    },
    esiaErrors: {
      path: '/auth/esia-errors',
      label: 'Ошибки ЕСИА',
    },
    authError: {
      path: '/auth/error',
      label: 'Ошибка авторизации',
    },
    registration: {
      path: '/auth/registration',
      label: 'Регистрация',
    },
  },

  application: {
    path: '/application/:id',
    getPath: (id: string) => `/application/${id}`,
    liquidityProviderList: {
      path: '/application/:id/liquidity-provider-list',
      getPath: (id: string) => `/application/${id}/liquidity-provider-list`,
    },
    liquidityProvider: {
      path: '/application/:id/liquidity-provider/:providerId',
      getPath: (id: string, providerId: string) =>
        `/application/${id}/liquidity-provider/${providerId}`,
    },
    liquidityProviderContract: {
      path: '/application/:id/liquidity-provider/:providerId/contract',
      getPath: (id: string, providerId: string) =>
        `/application/${id}/liquidity-provider/${providerId}/contract`,
    },
    form: {
      path: '/application/:id/form',
      getPath: (id: string) => `/application/${id}/form`,
    },
    contract: {
      path: '/application/:id/contract/:actionId',
      getPath: (id: string) => `/application/${id}/contract`,
    },
    edit: {
      path: '/application/:id/edit',
      getPath: (id: string) => `/application/${id}/edit`,
    },
    sign: {
      path: '/application/:id/sign/:actionId',
      getPath: (id: string, actionId: string) =>
        `/application/${id}/sign/${actionId}`,
    },
    upload: {
      path: '/application/:id/upload/:actionId',
      getPath: (id: string, actionId: string) =>
        `/application/${id}/upload/${actionId}`,
    },
    sendOffer: {
      path: '/application/:id/send-offer',
      getPath: (id: string) => `/application/${id}/send-offer`,
    },
    agentInfo: {
      path: '/application/:id/agent-info',
      getPath: (id: string) => `/application/${id}/agent-info`,
    },
    liquidityProviders: {
      path: '/application/:id/liquidity-providers',
      getPath: (id: string) => `/application/${id}/liquidity-providers`,
    },
    transactionId: {
      path: '/application/:id/transaction-id/:actionId',
      getPath: (id: string, actionId: string) =>
        `/application/${id}/transaction-id/${actionId}`,
    },
    offer: {
      path: '/application/:id/offer',
      getPath: (id: string) => `/application/${id}/offer`,
    },
    transfer: {
      path: '/application/:id/transfer',
      getPath: (id: string) => `/application/${id}/transfer`,
    },
    client: {
      path: '/application/:id/client',
      getPath: (id: string) => `/application/${id}/client`,
    },
    document: {
      path: '/application/:id/document/:documentId?',
      getPath: (id: string, documentId?: string) =>
        documentId
          ? `/application/${id}/document/${documentId}`
          : `/application/${id}/document`,
    },
    documentList: {
      path: '/application/:id/document',
      getPath: (id: string) => `/application/${id}/document`,
    },
    create: {
      path: '/application/create',
    },
  },

  rates: {
    path: '/rates',
    label: 'Курсы валют',
    create: {
      path: '/rates/create',
      label: 'Создать новый курс валюты',
    },
  },

  documents: {
    path: '/documents',
    label: 'Документы',
    documentsList: {
      path: '/documents',
      getPath: () => '/documents',
    },
  },

  profile: {
    path: '/profile',
    label: 'Профиль',
    getPath: () => `/profile`,
    profileEdit: {
      path: '/profile/edit',
    },
  },

  myApplicationList: {
    path: '/my-applications',
    label: 'Мои заявки',
  },

  notifications: {
    path: '/notifications',
    label: 'Уведомления',
    item: {
      path: '/notifications/:id',
      getPath: (id: string) => `/notifications/${id}`,
    },
  },

  help: {
    path: '/help',
    label: 'Помощь',
    faq: {
      path: '/help/faq',
    },
    enquiries: {
      path: '/help/enquiries',
      label: 'Запросы',
      item: {
        path: '/help/enquiries/:id',
        getPath: (id: string) => `/help/enquiries/${id}`,
      },
      create: {
        path: '/help/enquiries/create',
      },
    },
    tickets: {
      path: '/help/tickets',
      label: 'Обращения',
      item: {
        path: '/help/tickets/:id',
        getPath: (id: string) => `/help/tickets/${id}`,
      },
      create: {
        path: '/help/tickets/create',
      },
    },
  },

  balance: { path: '/balance', label: 'Мой баланс', getPath: () => '/balance' },

  dashboard: {
    path: '/',
  },

  root: { path: '/' },
};

export const ClientRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      <Route
        path="/auth"
        element={
          <CenteredLayout>
            <AuthPage />
          </CenteredLayout>
        }
      >
        <Route index element={<WelcomePage />} />
        <Route path="esia" element={<EsiaLoginPage />} />
        <Route path="ecp" element={<EcpLoginPage />} />
        <Route path="blocked" element={<Blocked />} />
        <Route path="blocked-user" element={<BlockedUserPage />} />
        <Route path="unavailable" element={<Unavailable />} />
        <Route path="esia-errors" element={<EsiaErrors />} />
        <Route path="error" element={<AuthError />} />
      </Route>

      <Route
        path="/registration"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/registration">
            <CenteredLayout>Регистрация</CenteredLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/dashboard"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <DashboardPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/application"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Outlet />
            </DefaultLayout>
          </AuthGuard>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route
          path="create"
          element={
            <AuthClient>
              <ApplicationFormation />
            </AuthClient>
          }
        />
        <Route
          path=":id/edit"
          element={
            <AuthClient>
              <ApplicationFormation />
            </AuthClient>
          }
        />
        <Route path=":id" element={<ApplicationLayout />}>
          <Route index element={<ApplicationClientDetails />} />
          <Route path="sign/:actionId" element={<SignDocumentPage />} />
          <Route path="upload/:actionId" element={<UploadDocumentPage />} />
          <Route path="send-offer" element={<SendOfferPage />} />
          <Route
            path="counterparty-offer"
            element={<CounterpartyOfferPage />}
          />
          <Route path="agent-info" element={<FormAgentInfoPage />} />
          <Route
            path="liquidity-providers"
            element={<ApplicationLiquidityProvidersPage />}
          />
          <Route
            path="transaction-id/:actionId"
            element={<TransactionIdPage />}
          />
          <Route
            path="contract/:actionId"
            element={<ApplicationContractPage />}
          />
          <Route path="client" element={<ApplicationClientPage />} />
          <Route path="offer" element={<SendOfferPage />} />
          <Route
            path="liquidity-provider/:providerId/contract"
            element={<ProviderContractPage />}
          />
          <Route path="transfer" element={<ApplicationTransferPage />} />
        </Route>
      </Route>

      {/* <Route
        path="/application/:id/liquidity-provider-list"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <ApplicationProvidersPage />
            </DefaultLayout>
          </AuthGuard>
        }
      /> */}

      <Route
        path="/rates/create"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <ExchanegeRatesCreatePage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/help"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <HelpCenterPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/help/tickets"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <TicketsListPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/help/tickets/create"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <TicketsCreatePage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/help/tickets/:id"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <TicketDetailPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/profile"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <ProfilePage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/profile/edit"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <ProfileEditPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/notifications"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <NotificationsPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/rates"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <RatesPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/documents"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <DocumentsPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/rates/currencyList"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <CurrencyListPage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/balance"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <BalancePage />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="/error/400"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Error400Page />
            </DefaultLayout>
          </AuthGuard>
        }
      />
      <Route
        path="/error/401"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Error401Page />
            </DefaultLayout>
          </AuthGuard>
        }
      />
      <Route
        path="/error/403"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Error403Page />
            </DefaultLayout>
          </AuthGuard>
        }
      />
      <Route
        path="/error/404"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Error404Page />
            </DefaultLayout>
          </AuthGuard>
        }
      />
      <Route
        path="/error/500"
        element={
          <AuthGuard checkFunction={checkToken} redirectToFail="/auth">
            <DefaultLayout>
              <Error500Page />
            </DefaultLayout>
          </AuthGuard>
        }
      />

      <Route
        path="*"
        element={
          <DefaultLayout>
            <Error404Page />
          </DefaultLayout>
        }
      />
    </Routes>
  );
};
